/*
 * ScrollArea
 */
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import { cn } from "./utils"
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from "react"

const ScrollArea = forwardRef<
	ElementRef<typeof ScrollAreaPrimitive.Root>,
	ScrollAreaProps
>(
	(
		{
			scrollBarProps = {},
			padScrollBar = true,
			viewportClassName,
			className,
			children,
			...props
		},
		ref,
	) => (
		<ScrollAreaPrimitive.Root
			ref={ref}
			className={cn("relative flex-1 overflow-hidden", className)}
			{...props}
		>
			<ScrollAreaPrimitive.Viewport
				className={cn(
					viewportClassName,
					"h-full w-full rounded-[inherit]",
					padScrollBar ? "pr-[10px]" : "",
				)}
			>
				{children}
			</ScrollAreaPrimitive.Viewport>
			<ScrollBar {...scrollBarProps} />
			<ScrollAreaPrimitive.Corner />
		</ScrollAreaPrimitive.Root>
	),
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

export type ScrollAreaProps = ScrollAreaPrimitive.ScrollAreaProps & {
	scrollBarProps?: ScrollBarProps
	padScrollBar?: boolean
	viewportClassName?: string
}

const ScrollBar = forwardRef<
	ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
	ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
	<ScrollAreaPrimitive.ScrollAreaScrollbar
		ref={ref}
		orientation={orientation}
		className={cn(
			"flex touch-none select-none transition-colors",
			orientation === "vertical" &&
				"h-full w-2.5 border-l border-l-transparent p-px",
			orientation === "horizontal" &&
				"h-2.5 flex-col border-t border-t-transparent p-px",
			className,
		)}
		{...props}
	>
		<ScrollAreaPrimitive.ScrollAreaThumb className="bg-paper-foreground relative flex-1 rounded-full" />
	</ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export type ScrollBarProps = ScrollAreaPrimitive.ScrollAreaScrollbarProps

export { ScrollArea, ScrollBar }
